import type { Doc } from "@convex/_generated/dataModel";
import { Info } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";

import type { CategoryListItem } from "~/api-refs";
import { Combobox } from "~/components/ui/combobox";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "~/components/ui/form";
import { Label } from "~/components/ui/label";
import { MultiCombobox } from "~/components/ui/multi-combobox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { taxCategoryDefault } from "~/utils/tax-category";

import type { ItemFormData } from "./types";

export const CategoryFields = ({
  form,
  categories,
  taxCategories,
  salesCategories,
  onCreateTaxCategory,
  onCreateSalesCategory,
}: {
  form: UseFormReturn<ItemFormData>;
  categories: CategoryListItem[] | undefined;
  taxCategories: Doc<"taxCategories">[];
  salesCategories: Doc<"salesCategories">[] | undefined;
  onCreateTaxCategory: (name: string) => void;
  onCreateSalesCategory: (name: string) => void;
}) => {
  const taxCategoriesList = [taxCategoryDefault, ...taxCategories];

  return (
    <>
      <Label className="flex items-center gap-1">
        Categories
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="cursor-help">
              <Info className="size-4" />
              <span className="sr-only">Hover for additional information</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Assign this item to menu categories</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </Label>
      <FormField
        control={form.control}
        disabled={!categories}
        name="categories"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <MultiCombobox
                groups={["default"]}
                items={{
                  default: categories
                    ? categories.map((category) => ({
                        value: category.categoryId ?? category.id,
                        label: category.name,
                        component: (
                          <div className="w-full space-y-1">
                            <div className="flex w-full items-baseline gap-1">
                              <p className="text-foreground">{category.name}</p>
                              {category.itemCount !== 0 && (
                                <p className="body-sm text-muted-foreground">
                                  {category.itemCount} item
                                  {category.itemCount !== 1 && "s"}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {category.menuRelations?.map((menuRelation) => (
                                <div
                                  className="caption bg-menu-bg text-menu rounded-full px-2 py-1"
                                  key={menuRelation.menu.id}
                                >
                                  {menuRelation.menu.name}
                                </div>
                              ))}
                            </div>
                          </div>
                        ),
                      }))
                    : [],
                }}
                onValueChange={(d) => field.onChange(d.default)}
                values={{
                  default: field.value ?? [],
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <Label className="flex items-center gap-1">
        Tax category
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="cursor-help">
              <Info className="size-4" />
              <span className="sr-only">Hover for additional information</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Product tax</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </Label>
      <FormField
        name="taxCategoryId"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Combobox
                className="w-full"
                createNewOption={{
                  label: "Create new category",
                  onClick: onCreateTaxCategory,
                }}
                dropdownClassName="w-130"
                items={
                  taxCategories?.length
                    ? taxCategoriesList.map((category) => ({
                        label: `${category.name} ${category.value} %`,
                        value: category._id,
                      }))
                    : [
                        {
                          label: `${taxCategoryDefault.name} ${taxCategoryDefault.value} %`,
                          value: taxCategoryDefault._id,
                        },
                      ]
                }
                onValueChange={(taxCategoryId) => {
                  if (taxCategoryId) {
                    form.setValue("taxCategoryId", taxCategoryId);
                  } else {
                    form.resetField("taxCategoryId");
                  }
                }}
                placeholder="Tax category name..."
                value={typeof field.value === "string" ? field.value : ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <Label className="flex items-center">
        Sales category
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="cursor-help">
              <Info className="size-4" />
              <span className="sr-only">Hover for additional information</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Sale category of the product</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </Label>
      <FormField
        name="salesCategoryId"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Combobox
                className="w-full"
                createNewOption={{
                  label: "Create new category",
                  onClick: onCreateSalesCategory,
                }}
                dropdownClassName="w-130"
                items={
                  salesCategories?.map((category) => ({
                    label: category.name,
                    value: category._id,
                  })) ?? []
                }
                onValueChange={(salesCategoryId) => {
                  if (salesCategoryId) {
                    form.setValue("salesCategoryId", salesCategoryId);
                  } else {
                    form.resetField("salesCategoryId");
                  }
                }}
                placeholder="Sales category name..."
                value={typeof field.value === "string" ? field.value : ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};
